import React from 'react';
import { Status } from '@/components';
import { EscalationStatus } from '@/types/rental';
import { formatRupiah } from '@/utils/format';
import {
  AreaGroup,
  AreaLevel,
  TRACKING_NO_VALUE,
} from '../rentTrackingDisplay';

export interface AreaBreakdownPanelProps {
  groups: AreaGroup[];
  level: AreaLevel;
  onLevelChange: (level: AreaLevel) => void;
  /** Codes currently filtered on, so the active rows read as selected. */
  selectedCodes: string[];
  onToggle: (code: string) => void;
  /** Opens one unit's panel from the drill-down list. */
  onSelectUnit: (rentalID: string) => void;
}

const LEVELS: { key: AreaLevel; label: string }[] = [
  { key: 'city', label: 'Kabupaten/kota' },
  { key: 'district', label: 'Kecamatan' },
];

/**
 * Units per area, biggest arrears first, so a field trip can be planned by
 * where the units are instead of by reading pins off the map. Clicking a row
 * filters everything — map, table and summary — to that area.
 */
export const AreaBreakdownPanel: React.FC<AreaBreakdownPanelProps> = ({
  groups,
  level,
  onLevelChange,
  selectedCodes,
  onToggle,
  onSelectUnit,
}) => (
  <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white/95 shadow-lg backdrop-blur">
    <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
      <div>
        <p className="text-[14px] font-medium text-gray-900">Sebaran area</p>
        <p className="text-[12px] text-gray-500">
          Dari posisi terakhir tiap unit, urut tunggakan terbesar. Klik satu
          area untuk menyaring peta dan melihat unitnya.
        </p>
      </div>
      <div className="flex flex-shrink-0 rounded-lg border border-gray-200 p-0.5">
        {LEVELS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onLevelChange(option.key)}
            className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
              level === option.key
                ? 'bg-[#5e2aac] text-white'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>

    {groups.length === 0 ? (
      <p className="px-3 py-4 text-[13px] text-gray-500">
        Tidak ada unit untuk dikelompokkan.
      </p>
    ) : groups.length === 1 && groups[0].code === TRACKING_NO_VALUE ? (
      // One "no area" group for the whole fleet is not a fact about the units:
      // it means nothing has been resolved into an area yet.
      <div className="px-3 py-4 text-[13px] text-gray-500">
        <p className="text-gray-900">
          {groups[0].units} unit, belum ada satu pun yang punya area.
        </p>
        <p className="mt-1 text-[12px]">
          Area diisi oleh job penyelaras zona di backend, bukan oleh halaman
          ini. Kalau fiturnya baru naik, area muncul setelah job itu jalan
          pertama kali.
        </p>
      </div>
    ) : (
      <ul className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto">
        {groups.map((group) => {
          const isSelected = selectedCodes.includes(group.code);
          return (
            <li key={group.code}>
              <button
                type="button"
                onClick={() => onToggle(group.code)}
                aria-pressed={isSelected}
                className={`flex w-full items-start justify-between gap-3 px-3 py-2 text-left transition-colors ${
                  isSelected ? 'bg-[#f4efff]' : 'hover:bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium text-gray-900">
                    {group.name}
                    {group.code !== TRACKING_NO_VALUE && (
                      <span className="ml-1 font-normal text-gray-400">
                        {group.code}
                      </span>
                    )}
                  </p>
                  <p className="text-[12px] text-gray-500">
                    {group.units} unit
                    {group.drawn < group.units
                      ? ` · ${group.drawn} di peta`
                      : ''}
                    {group.withCase > 0 ? ` · ${group.withCase} case repo` : ''}
                  </p>
                </div>
                <span className="flex-shrink-0 text-[12px] font-medium text-gray-900">
                  {formatRupiah(group.arrears)}
                </span>
              </button>

              {/* The units themselves, so picking an area answers "which
                  bikes?" without going to the table. */}
              {isSelected && (
                <ul className="border-t border-gray-100 bg-white">
                  {group.members.map((unit) => (
                    <li key={unit.rentalID}>
                      <button
                        type="button"
                        onClick={() => onSelectUnit(unit.rentalID)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 pl-6 text-left hover:bg-gray-50"
                      >
                        <span className="min-w-0">
                          <span className="text-[12px] font-medium text-gray-900">
                            {unit.platNomor ?? unit.rentalCode}
                          </span>
                          {unit.escalationStatus !==
                            EscalationStatus.NORMAL && (
                            <span className="ml-1.5 align-middle">
                              <Status
                                value={unit.escalationStatus}
                                type="escalationStatus"
                                size="small"
                              />
                            </span>
                          )}
                          <span className="block truncate text-[11px] text-gray-500">
                            {unit.driverName ?? '-'}
                          </span>
                        </span>
                        <span className="flex-shrink-0 text-right">
                          <span className="block text-[12px] text-gray-900">
                            {formatRupiah(unit.arrears.amount)}
                          </span>
                          <span className="block text-[11px] text-gray-500">
                            {unit.arrears.days} hari
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

export default AreaBreakdownPanel;
